import React, { useEffect, useState, useReducer } from 'react';
import SwiperClass from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { useRouter } from 'next/router';

import { useWindowSize } from '@/components/use.window.size';
import { LayoutProps } from '../layout.props';
import { Field } from './field';
import { FormPage } from './page';
import { Header } from '@/components/header';
import { ProgressHeader } from '@/components/ProgressHeader';
import { useSubmission } from '@/components/use.submission';
import SubmitModal from '@/components/SubmitModal';
import {
  ACTIONS,
  SubmissionsState,
  SubmissionsAction,
} from '@/components/layouts/layout.props';
import { CustomStartPage } from '@/components/custom/gen-ai-readiness/start-page';

/*
================================================================================================================================
================================================================================================================================

changes to this file must also be reflected in ../../custom\product-shape/form.tsx

================================================================================================================================
================================================================================================================================
*/

const reducer = (
  submissions: SubmissionsState[],
  action: SubmissionsAction
) => {
  switch (action.type) {
    case ACTIONS.ADD_SUBMISSION:
      let replaced = false;
      const updated = submissions.map((submission: SubmissionsState) => {
        if (submission.field === action.payload.field) {
          replaced = true;
          return action.payload;
        } else {
          return submission;
        }
      });
      if (replaced) {
        return updated;
      } else {
        return [...updated, action.payload];
      }
    case ACTIONS.DELETE_SUBMISSIONS:
      return [];
    default:
      return submissions;
  }
};

export const SliderLayout: React.FC<LayoutProps> = ({
  form,
  renderCustomFormEndPage,
  renderCustomFormStartPage,
  customFormJson,
  startPageFields,
}) => {
  // const { t } = useTranslation();
  const router = useRouter();
  const [swiper, setSwiper] = useState<SwiperClass>();
  const { height } = useWindowSize();
  const { id, design, startPage, endPage, fields, isLive } = form;
  const [submissionCounter, setSubmissionCounter] = useState(0); // current overall submission number, 1st, 2nd, etc
  const [submissionFlag, setSubmissionFlag] = useState(false); // flag to whether submit the submission or not
  const currentSubmission = useSubmission(id as string);
  const { startSubmission, finish, setField } = currentSubmission;
  const [submissions, dispatch] = useReducer(reducer, []); // overall submissions
  // the count of fields that are not hidden
  // a let variable so that every time this component is re-rendered, it starts from 0 again to render the slides correctly
  let actualFieldCount = 0;
  const [showProgress, setShowProgress] = useState(!startPage.show);
  const [progress, setProgress] = useState(0);
  const [lastSlideIndex, setLastSlideIndex] = useState(fields.length - 1);
  const [startPageSubmissions, setStartPageSubmissions] = useState([]);
  const [genAiCalcData, setGenAiCalcData] = useState<any>([]);
  //track total questions
  const totalQuestions = fields.filter(
    (field) => field.type !== 'hidden'
  ).length;

  useEffect(() => {
    document.title = form.title;

    // get the slide index of the last slide that is not hidden
    for (let i = fields.length - 1; i >= 0; i--) {
      if (fields[i].type !== 'hidden') {
        setLastSlideIndex(i);
        break;
      }
    }
  }, []);

  useEffect(() => {
    const submitSubmission = async () => {
      const s: { id: string; token: string } | undefined =
        await startSubmission();
      if (s) {
        // Needed to get update the submissions for the GenAI Calc page to include data from start page form
        if (router.query.id === 'ejKQZ1') {
          const genAiCalcSubmissions = submissions.map((item: any) => {
            const sp = startPageSubmissions.find((startPageItem: any) => {
              if (item.field === startPageItem.field) {
                return startPageItem;
              }
            });
            if (sp) {
              return sp;
            }
            return item;
          });
          setGenAiCalcData(genAiCalcSubmissions);
          await setField('', genAiCalcSubmissions, s).catch((err) =>
            console.error(err)
          );
        } else {
          await setField('', submissions, s).catch((err) => console.error(err));
        }
        await finish(s);
      }
      setProgress(100);
      setShowProgress(false);
      setSubmissionFlag(false);
      // saving could be really fast so this prevents saving spinner from flashing
      await new Promise((r) => setTimeout(r, 1000));
      goNext();
    };
    if (submissionFlag) {
      submitSubmission();
    }
  }, [submissionFlag]);

  // mui text input (textarea, textfield) does not go back to its default value even though we pass in empty values and re-render the components
  // this hook ensures that when the form is resetted, the values in the text inputs do not hold the previous user inputs
  useEffect(() => {
    if (submissionCounter !== 0) {
      Array.from(document.getElementsByClassName('MuiInputBase-input')).forEach(
        (textfield) => {
          const converted = textfield as HTMLInputElement;
          converted.value = '';
        }
      );
    }
  }, [submissionCounter]);

  const goNext = () => {
    if (!swiper) return;

    // logger('goNext')
    swiper.allowSlideNext = true;
    swiper.slideNext();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlideNext = false;
  };
  const goPrev = () => {
    if (!swiper) return;

    // logger('goPrev')
    swiper.allowSlidePrev = true;
    swiper.slidePrev();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };
  const goBeginning = () => {
    if (!swiper) return;

    // logger('goNext')
    setProgress(0);
    setShowProgress(!startPage.show);
    dispatch({
      type: ACTIONS.DELETE_SUBMISSIONS,
      payload: { field: '', data: '' },
    });
    setSubmissionCounter(submissionCounter + 1);
    swiper.allowSlidePrev = true;
    swiper.slideTo(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    swiper.allowSlidePrev = false;
  };

  const swiperConfig: SwiperProps = {
    direction: 'horizontal',
    allowSlideNext: false,
    allowSlidePrev: false,
    preventClicks: false,
    noSwiping: false,
    allowTouchMove: false,
    preventClicksPropagation: false,
    updateOnWindowResize: true,
    keyboard: false, // doesn't seem to do anything but will leave here, handleKeyPress is custom way of disable keyboard
  };

  const handleKeyPress = async (event: any) => {
    if (
      // prevent user from going forward from pressing enter key
      // however it will allow enter key to go through if the event came from mui textarea
      // this is to allow the textarea to add new lines
      event.key === 'Enter' &&
      !event.target.className.includes('MuiInputBase-inputMultiline')
    ) {
      event.preventDefault();
    } else if (
      // prevent user from going forward from pressing space key
      // howeever it will allow space key to go through if the event came from mui input
      // this is to allow text line, text area, etc to add space character
      event.key === ' ' &&
      !event.target.className.includes('MuiInputBase-input')
    ) {
      event.preventDefault();
    }
  };

  const addHiddenSubmission = (hiddenObject: any) => {
    // we must check if the hidden submission is already in the submissions array
    // this is because we add the hidden submission (using setSubmissions) when the field is rendering and the type is hidden
    // setSubmissions causes a re-render of this component and this re-renders the fields again, causing hidden submission to be added again
    // thus becomes an infinite loop
    for (let i = 0; i < submissions.length; i++) {
      if (
        submissions[i].field === hiddenObject.field &&
        submissions[i].data === hiddenObject.data
      ) {
        return;
      }
    }
    dispatch({
      type: ACTIONS.ADD_SUBMISSION,
      payload: hiddenObject,
    });
  };

  return (
    <>
      {submissionFlag && <SubmitModal design={design} />}
      <div
        className={'swiper-container'}
        style={{
          background: design.colors.background,
        }}
        onKeyDown={handleKeyPress}
      >
        {showProgress ? (
          <ProgressHeader
            progressValue={progress}
            steps={fields.length}
            design={design}
            reset={goBeginning}
          />
        ) : null}

        <Swiper
          autoHeight // for some reason causes speedup transition for start page?
          {...swiperConfig}
          onSwiper={(next) => {
            // logger('setSwiper')
            setSwiper(next);
          }}
        >
          {[
            startPage.show ? (
              <SwiperSlide key={'start'}>
                {renderCustomFormStartPage && (
                  <CustomStartPage
                    page={startPage}
                    className={'start-page'}
                    image={true}
                    design={design}
                    next={goNext}
                    showProgress={setShowProgress}
                    startPageFields={startPageFields}
                    setStartPageSubmissions={setStartPageSubmissions}
                  />
                )}
                {!renderCustomFormStartPage && (
                  <FormPage
                    page={startPage}
                    className={'start-page'}
                    image={true}
                    design={design}
                    next={goNext}
                    prev={goBeginning}
                    showProgress={setShowProgress}
                  />
                )}
              </SwiperSlide>
            ) : undefined,
            ...fields
              .map((field, i) => {
                if (field.type === 'hidden') {
                  addHiddenSubmission({
                    field: field.id,
                    data: field.defaultValue,
                  });
                  return null;
                }
                actualFieldCount += 1; // add 1 to count the current field we should render (does not count hiddens)

                return (
                  <SwiperSlide key={field.id} className={'slide'}>
                    <Field
                      field={field}
                      focus={
                        swiper?.activeIndex === (startPage.show ? 1 : 0) + i
                      }
                      design={design}
                      save={(values: { [key: string]: unknown }) => {
                        const fieldObj = {
                          field: field.id,
                          data: JSON.stringify(
                            values[field.id] !== undefined &&
                              values[field.id] !== null
                              ? values[field.id]
                              : '-'
                          ),
                        };

                        dispatch({
                          type: ACTIONS.ADD_SUBMISSION,
                          payload: fieldObj,
                        });

                        if (i === lastSlideIndex && isLive) {
                          setSubmissionFlag(true);
                        }
                      }}
                      next={() => {
                        if (i === lastSlideIndex) {
                          // prevent going back!
                          swiper!.allowSlidePrev = false;
                          setShowProgress(false);
                        }
                        setProgress(
                          Math.floor(((i + 1) / fields.length) * 100)
                        );
                        goNext();
                      }}
                      prev={() => {
                        if (actualFieldCount - 1 !== 0) {
                          swiper!.allowSlideNext = false;
                          setProgress(
                            Math.floor(((i - 1) / fields.length) * 100)
                          );
                          goPrev();
                        }
                      }}
                      slideIndex={actualFieldCount - 1}
                      submissionCount={submissionCounter}
                      lastSlideIndex={lastSlideIndex}
                      //add pass total q
                      totalQuestions={totalQuestions}
                    />
                  </SwiperSlide>
                );
              })
              .filter((e) => e !== null),
            !submissionFlag && progress === 100 && (
              <SwiperSlide key={'end'}>
                {renderCustomFormEndPage && (
                  <>
                    {renderCustomFormEndPage({
                      design: design,
                      jsonData: customFormJson,
                      questions: fields,
                      userAnswers: genAiCalcData,
                      className: 'end-page-calc',
                    })}
                  </>
                )}
                {!renderCustomFormEndPage && (
                  <>
                    <Header design={design} />
                    <FormPage
                      page={endPage}
                      className={'end-page'}
                      design={design}
                      next={goNext}
                      prev={goBeginning}
                      showProgress={setShowProgress}
                    />
                  </>
                )}
              </SwiperSlide>
            ),
          ].filter((e) => !!e)}
        </Swiper>
      </div>
    </>
  );
};
